import { supabase } from './supabaseClient';

/**
 * Access Control Service
 * Centralized enforcement of subscription and access code requirements
 */

export interface AccessCheckResult {
  hasAccess: boolean;
  reason?: string;
  expiresAt?: string;
  accountId?: string;
}

/**
 * Checks if a user has valid access to the platform
 * Returns access status and reason if denied
 */
export async function checkUserAccess(userId: string): Promise<AccessCheckResult> {
  try {
    // Get user's account
    const { data: accounts, error: accountError } = await supabase
      .from('accounts')
      .select('id, subscription_status, subscription_tier, trial_end_date, beta_access_end_date, stripe_subscription_id')
      .eq('owner_id', userId)
      .limit(1);

    if (accountError || !accounts || accounts.length === 0) {
      return {
        hasAccess: false,
        reason: 'No account found'
      };
    }

    const account = accounts[0];
    const now = new Date();

    // Check 1: Active paid subscription
    if (account.stripe_subscription_id && account.subscription_status === 'active') {
      return {
        hasAccess: true,
        accountId: account.id
      };
    }

    // Check 2: Active access code grant
    const { data: grants, error: grantError } = await supabase
      .from('account_access_grants')
      .select('expires_at, status')
      .eq('account_id', account.id)
      .eq('status', 'active')
      .limit(1);

    if (!grantError && grants && grants.length > 0) {
      const grant = grants[0];
      const expiresAt = new Date(grant.expires_at);

      if (expiresAt > now) {
        return {
          hasAccess: true,
          expiresAt: grant.expires_at,
          accountId: account.id
        };
      } else {
        return {
          hasAccess: false,
          reason: 'Access code expired'
        };
      }
    }

    // Check 3: Active trial period
    if (account.trial_end_date) {
      const trialEnd = new Date(account.trial_end_date);

      if (trialEnd > now) {
        return {
          hasAccess: true,
          expiresAt: account.trial_end_date,
          accountId: account.id
        };
      } else {
        return {
          hasAccess: false,
          reason: 'Trial period expired'
        };
      }
    }

    // Check 4: Legacy beta access
    if (account.beta_access_end_date) {
      const betaEnd = new Date(account.beta_access_end_date);

      if (betaEnd > now) {
        return {
          hasAccess: true,
          expiresAt: account.beta_access_end_date,
          accountId: account.id
        };
      } else {
        return {
          hasAccess: false,
          reason: 'Beta access expired'
        };
      }
    }

    // No valid access method found
    return {
      hasAccess: false,
      reason: 'No active subscription, trial, or access code'
    };
  } catch (error) {
    console.error('Error checking user access:', error);
    return {
      hasAccess: false,
      reason: 'Error checking access'
    };
  }
}

/**
 * Middleware-style function for API routes
 * Returns user ID if access is valid, throws error if not
 */
export async function requireAccess(authHeader: string | null): Promise<string> {
  if (!authHeader) {
    throw new Error('No authorization header');
  }

  const token = authHeader.replace('Bearer ', '');
  const { data: { user }, error: authError } = await supabase.auth.getUser(token);

  if (authError || !user) {
    throw new Error('Unauthorized');
  }

  const accessCheck = await checkUserAccess(user.id);

  if (!accessCheck.hasAccess) {
    throw new Error(`Access denied: ${accessCheck.reason}`);
  }

  return user.id;
}
